import Decimal from 'decimal.js'
import { PositionMetricsGenerationError } from './errors'

interface InventoryEntry {
  amount: bigint
  wac: Decimal
}

export interface SerializedInventory {
  entries: { key: string; amount: string; wac: string }[]
  realizedPnl: string
}

export class Inventory {
  private inventory: Map<string, InventoryEntry>
  private realizedPnl: Decimal

  constructor() {
    this.inventory = new Map()
    this.realizedPnl = new Decimal(0)
  }

  add(key: string, amount: bigint, priceNumeric: Decimal) {
    if (amount === 0n) {
      return
    }
    if (!priceNumeric.isFinite()) {
      throw new PositionMetricsGenerationError('Inventory.add: priceNumeric must be finite')
    }

    const entry = this.inventory.get(key)
    if (!entry) {
      this.inventory.set(key, { amount, wac: priceNumeric })
    } else {
      const newAmount = entry.amount + amount
      if (newAmount === 0n) {
        this.inventory.delete(key)
        return
      }

      const newWac = entry.wac
        .mul(entry.amount.toString())
        .add(priceNumeric.mul(amount.toString()))
        .div(newAmount.toString())
      this.inventory.set(key, { ...entry, amount: newAmount, wac: newWac })
    }
  }

  remove(key: string, amount: bigint, priceNumeric: Decimal) {
    if (amount === 0n) {
      return
    }
    if (!priceNumeric.isFinite()) {
      throw new PositionMetricsGenerationError('Inventory.remove: priceNumeric must be finite')
    }

    let entry = this.inventory.get(key)

    if (!entry) {
      entry = { amount: 0n, wac: new Decimal(0) }
    }

    const newAmount = entry.amount - amount
    if (newAmount === 0n) {
      this.inventory.delete(key)
    } else {
      this.inventory.set(key, { ...entry, amount: newAmount })
    }

    const realizedPnl = priceNumeric.sub(entry.wac).mul(amount.toString())

    this.realizedPnl = this.realizedPnl.add(realizedPnl)

    return realizedPnl
  }

  getRealizedPnl() {
    return this.realizedPnl
  }

  calcUnrealizedPnl(pricesNumeric: Map<string, Decimal>) {
    let unrealizedPnl = new Decimal(0)
    for (const [coinType, entry] of this.inventory.entries()) {
      const price = pricesNumeric.get(coinType)
      if (!price) {
        throw new PositionMetricsGenerationError(`Price not found for "${coinType}"`)
      }

      unrealizedPnl = unrealizedPnl.add(price.sub(entry.wac).mul(entry.amount.toString()))
    }
    return unrealizedPnl
  }

  closeAll(pricesNumeric: Map<string, Decimal>) {
    for (const [key, entry] of this.inventory.entries()) {
      const price = pricesNumeric.get(key)
      if (!price) {
        throw new PositionMetricsGenerationError(`Price not found for "${key}"`)
      }
      const pnl = price.sub(entry.wac).mul(entry.amount.toString())
      this.realizedPnl = this.realizedPnl.add(pnl)
    }
    this.inventory.clear()
  }

  serialize(): SerializedInventory {
    const entries = Array.from(this.inventory.entries()).map(([key, entry]) => ({
      key,
      amount: entry.amount.toString(),
      wac: entry.wac.toString(),
    }))
    return { entries, realizedPnl: this.realizedPnl.toString() }
  }

  static deserialize(data: SerializedInventory): Inventory {
    const inv = new Inventory()
    for (const entry of data.entries) {
      inv.inventory.set(entry.key, {
        amount: BigInt(entry.amount),
        wac: new Decimal(entry.wac),
      })
    }
    inv.realizedPnl = new Decimal(data.realizedPnl)
    return inv
  }

  calcEquity(pricesNumeric: Map<string, Decimal>) {
    let equity = new Decimal(0)
    for (const [coinType, entry] of this.inventory.entries()) {
      const price = pricesNumeric.get(coinType)
      if (!price) {
        throw new PositionMetricsGenerationError(`Price not found for "${coinType}"`)
      }
      equity = equity.add(price.mul(entry.amount.toString()))
    }
    return equity
  }
}
